// src/pages/chat/settings/settings.hooks.ts
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useReducedMotion, Variants } from "framer-motion";
import {
    BillingDetails,
    billingDetailsApi,
    passkeyApi,
    PaymentInfo,
    paymentsApi,
} from "../../../api";
import { useAuth } from "../../../contexts/auth/auth-context";
import { billingModalOpenRef } from "./modal-context";
import { isNative } from "../../../platform";

export type SettingsTab =
    | "profile"
    | "notifications"
    | "account"
    | "privacy"
    | "billing"
    | "change-password"
    | "compare-plans";

type UseSettingsModalArgs = {
    onClose: () => void;
    open: boolean;
    initialTab?: SettingsTab | undefined;
};

export function useSettingsModal(
    { onClose, open, initialTab }: UseSettingsModalArgs,
) {
    const [tab, setTab] = useState<SettingsTab>(initialTab ?? "profile");
    const [openSanctumUpgradeNow, setOpenSanctumUpgradeNow] = useState(false);
    const [paymentInfo, setPaymentInfo] = useState<PaymentInfo | null>(null);
    const prefersReduced = useReducedMotion();

    const { profile: authProfile } = useAuth();

    // State for pre-fetched data
    const [profileData, setProfileData] = useState<any>(null);
    const [notificationsData, setNotificationsData] = useState<
        { notification_method: string | null; sms_consent: boolean }
    >({ notification_method: null, sms_consent: false });
    const [accountData, setAccountData] = useState<
        { passkeys: any[]; auto_prompt_passkey: boolean }
    >({ passkeys: [], auto_prompt_passkey: true });
    const [billingDetails, setBillingDetails] = useState<BillingDetails | null>(
        null,
    );
    const [hasPrefetched, setHasPrefetched] = useState(false);
    const [isLoadingPasskeys, setIsLoadingPasskeys] = useState(false);
    const [isLoadingPayment, setIsLoadingPayment] = useState(false);
    const [isLoadingBillingDetails, setIsLoadingBillingDetails] = useState(
        false,
    );

    const prevOpenRef = useRef(open);
    useEffect(() => {
        const wasOpen = prevOpenRef.current;
        prevOpenRef.current = open;

        // Only set tab on "open" transition
        if (!wasOpen && open) {
            setTab(initialTab ?? "profile");
        }
    }, [open, initialTab]);

    useEffect(() => {
        if (!open) {
            // Reset prefetch flag when modal closes so it refetches on next open
            setHasPrefetched(false);
            return;
        }

        // Only prefetch once per modal open
        if (hasPrefetched) return;

        // Use profile from auth context immediately (no loading needed)
        if (authProfile) {
            setProfileData(authProfile);

            // Set notifications data from profile
            setNotificationsData({
                notification_method: authProfile.notification_method || null,
                sms_consent: authProfile.sms_consent || false,
            });

            // Set account data (passkeys will be loaded later)
            setAccountData({
                passkeys: [],
                auto_prompt_passkey: authProfile.auto_prompt_passkey ?? true,
            });
        }

        // Prefetch remaining data in background (no loading UI)
        const prefetchData = async () => {
            setIsLoadingPayment(true);
            setIsLoadingPasskeys(true);
            setIsLoadingBillingDetails(true);
            try {
                const [paymentInfo, passkeys, billing] = await Promise.all([
                    paymentsApi
                        .getMe()
                        .catch((err: any) => {
                            console.error("Failed to load payment info:", err);
                            return null;
                        })
                        .finally(() => setIsLoadingPayment(false)),
                    passkeyApi
                        .listCredentials()
                        .catch((err: any) => {
                            console.error("Failed to load passkeys:", err);
                            return [];
                        })
                        .finally(() => setIsLoadingPasskeys(false)),
                    billingDetailsApi
                        .get()
                        .catch((err: any) => {
                            console.error(
                                "Failed to load billing details:",
                                err,
                            );
                            return null;
                        })
                        .finally(() => setIsLoadingBillingDetails(false)),
                ]);

                setPaymentInfo(paymentInfo);

                // Update account data with loaded passkeys
                setAccountData((prev) => ({
                    ...prev,
                    passkeys: passkeys || [],
                }));

                setBillingDetails(billing);
                setHasPrefetched(true);
            } catch (error) {
                console.error("Failed to prefetch settings data:", error);
            }
        };

        prefetchData();
    }, [open, hasPrefetched, authProfile]);

    // Keep profileData in sync when authProfile changes (e.g. after email/phone change)
    useEffect(() => {
        if (authProfile && hasPrefetched) {
            setProfileData(authProfile);
        }
    }, [authProfile]);

    // Callback for notifications tab to update local state after saving
    // This keeps notificationsData in sync without relying on authProfile changes
    const handleNotificationsChanged = useCallback(
        (method: string | null, consent: boolean) => {
            setNotificationsData({
                notification_method: method,
                sms_consent: consent,
            });
        },
        [],
    );

    const tabs: {
        key: Exclude<SettingsTab, "change-password" | "compare-plans">;
        label: string;
    }[] = useMemo(
        () => [
            { key: "profile", label: "Profile" },
            { key: "notifications", label: "Notifications" },
            { key: "account", label: "Account" },
            { key: "privacy", label: "Privacy" },
            ...(!isNative() ? [{ key: "billing" as const, label: "Billing" }] : []),
        ],
        [],
    );

    const handleBack = useCallback(() => {
        if (tab === "change-password") {
            setTab("account");
        } else if (tab === "compare-plans") {
            setTab("billing");
        } else {
            onClose();
        }
    }, [tab, onClose]);

    const handleOpenChange = useCallback(
        (open: boolean) => {
            // Don't close settings while a billing modal portal is active
            if (!open && billingModalOpenRef.current) return;
            if (!open) {
                onClose();
            }
        },
        [onClose],
    );

    const variants: Variants = useMemo(
        () =>
            prefersReduced
                ? {
                    initial: { opacity: 0 },
                    animate: { opacity: 1, transition: { duration: 0.22 } },
                    exit: { opacity: 0, transition: { duration: 0.18 } },
                }
                : {
                    initial: {
                        opacity: 0,
                        y: 10,
                        scale: 0.995,
                        filter: "blur(1px)",
                    },
                    animate: {
                        opacity: 1,
                        y: 0,
                        scale: 1,
                        filter: "blur(0px)",
                        transition: {
                            type: "spring",
                            stiffness: 120,
                            damping: 18,
                        },
                    },
                    exit: {
                        opacity: 0,
                        y: -6,
                        scale: 0.997,
                        filter: "blur(0.5px)",
                        transition: { duration: 0.18, ease: "easeOut" },
                    },
                },
        [prefersReduced],
    );

    const isHiddenTab = tab === "change-password" || tab === "compare-plans";

    const goToChangePassword = useCallback(() => {
        setTab("change-password");
    }, []);

    const goToComparePlans = useCallback(() => {
        setTab("compare-plans");
    }, []);

    const handlePasswordChanged = useCallback(() => {
        setTab("account");
    }, []);

    const triggerSanctumUpgrade = useCallback(() => {
        setOpenSanctumUpgradeNow(true);
        setTab("billing");
    }, []);

    const handleExternalOpenSanctumUpgradeHandled = useCallback(() => {
        setOpenSanctumUpgradeNow(false);
    }, []);

    return {
        tab,
        setTab,
        tabs,
        isHiddenTab,
        variants,
        handleBack,
        handleOpenChange,
        openSanctumUpgradeNow,
        handleExternalOpenSanctumUpgradeHandled,
        goToChangePassword,
        goToComparePlans,
        handlePasswordChanged,
        triggerSanctumUpgrade,
        paymentInfo,
        profileData,
        notificationsData,
        accountData,
        billingDetails,
        isLoadingPasskeys,
        isLoadingPayment,
        isLoadingBillingDetails,
        handleNotificationsChanged,
    };
}
